import { Injectable, Injector } from '@angular/core';
import { ApplicationService } from '@app/shared/application/application.service';
import { RequirementConfigurationService } from '@app/shared/services/requirement-configuration.service';
import { AppComponentBase } from '@shared/common/app-component-base';
import { ApplicationStatus, ApplicationType, ApplicationUploadFilesInput, ExternalSystemEnum, GeneralFileDto, GeneralFileInfoDto, GetLegOutput, LegDocumentGroup, LegDto, LegPersonDto, LegRegisterOutput, LegServiceProxy, LegSponsorshipDto, LegStatus, PersonApplicationSummaryDto, SponsorshipApplicationSummaryDto, UpdateLegSponsorshipInput } from '../../../shared/service-proxies/service-proxies';
import { delay, map, Observable, of, tap } from 'rxjs';

@Injectable({providedIn: 'root'})
export class LegService extends AppComponentBase {

    leg: LegDto;
    // liaResultDocuments: GeneralFileInfoDto[];

    constructor(
        injector: Injector,
        private applicationService: ApplicationService,
        private requirementConfigurationService: RequirementConfigurationService,
        private legService: LegServiceProxy
    ) {
        super(injector);
        console.log('LegService constructor');
    }

    createLeg(applicationType: ApplicationType): Observable<string> {
        this.closeLeg();
        return this.legService.createLeg(applicationType).pipe();
    }

    getLeg(caseId: string): Observable<GetLegOutput> {
        this.closeLeg();
        return this.legService.getLeg(caseId).pipe(
            tap((output: GetLegOutput) => {
                this.leg = output.leg;
            })
        );
    }

    closeLeg(): void {
        this.leg = null;
    }

    validateGlnForLegitimaiton(gln: string, professionId: string): Observable<LegRegisterOutput> {
        return this.legService.validateGlnForLegitimation(gln, professionId, this.caseId).pipe();
    }

    getNextStatus(status: LegStatus): Observable<LegStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.legService.getNextStatus(this.caseId, status).pipe(map(status => {
            if (isAuthority) {
                return LegStatus.FinishReview;
            }
            return status;
        }));
    }

    getPerson(): Observable<LegPersonDto> {
        return of(this.leg.legPerson).pipe(delay(1));
    }

    getDocuments(status: LegStatus): Observable<LegDocumentGroup[]> {
        return this.legService.getDocumentsForLeg(this.caseId, status);
    }

    getFile(fileId: number, step: LegStatus): Observable<GeneralFileDto> {
        return this.legService.getFile(this.caseId, step, fileId);
    }

    uploadFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.legService.uploadFiles(input);
    }

    deleteFile(id: number, step: LegStatus): Observable<void> {
        return this.legService.deleteFile(this.caseId, step, id);
    }

    checkDocuments(): Observable<LegStatus[]> {
        return this.legService.validateThatAllMandatoryDocumentsHaveBeenUploaded(this.caseId);
    }

    releaseLeg(comment: string): Observable<void> {
        return this.legService.releaseLeg(this.caseId, comment);
    }

    getExternalSystemByProfessionId(professionId: string): Observable<ExternalSystemEnum> {
        return this.legService.getExternalRegisterByProfessionId(professionId);
    }

    acceptRegisterCheck(): Observable<LegStatus> {
        this.applicationService.setStepToReview(LegStatus.Person);
        return this.legService.acceptRegisterCheck(this.caseId).pipe(tap((status: LegStatus) => {
            this.leg.currentStep = status;
        }));
    }

    getStepUrl(nextStep: LegStatus): string {
        return this.applicationService.getNextStep(LegService.getUrlForStep(nextStep));
    }

    static getUrlForStep(step: LegStatus): string {
        switch (step) {
            case LegStatus.FinishReview:
                return 'finish-review';
            case LegStatus.Person:
                return 'person';
            case LegStatus.Sponsorship:
                return 'sponsorship';
            case LegStatus.Release:
                return 'release';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }

    get caseId(): string {
        return this.leg?.application?.caseId;
    }

    getApplications (filter: string, sorting: string, maxResultCount: number, skipCount: number){
        return this.legService.getLegApplications(filter, sorting, maxResultCount, skipCount);
    }

    getEvaluationComments(): Observable<string> {
        return this.legService.getEvaluationComments(this.applicationService.caseId);
    }

    getPersonId(): Observable<number> {
        return this.legService.getPersonId(this.applicationService.caseId).pipe(delay(1));
    }

    get status(): ApplicationStatus {
        return this.leg?.application.status;
    }
    
    getSponsorshipId(): Observable<number> {
        return this.legService.getSponsorshipId(this.applicationService.caseId).pipe(delay(1));
    }

    getSponsorship(): Observable<LegSponsorshipDto> {
        return of(this.leg.legSponsorship).pipe(delay(1));
    }

    updateLegSponsorship(input: UpdateLegSponsorshipInput): Observable<LegStatus> {
        return this.legService.updateSponsorship(input).pipe();
    }

    getMyPersonLegs(): Observable<PersonApplicationSummaryDto[]> {
        return this.legService.getMyPersonLegs().pipe();
    }

    getMySponsorshipLegs(): Observable<SponsorshipApplicationSummaryDto[]> {
        return this.legService.getMySponsorshipLegs().pipe();
    }
}
